import React, { Component } from "react";
import { findDOMNode } from "react-dom";
import { withTranslation, WithTranslation } from "react-i18next";
import { analyticsFEA } from "src/AnalyticsFEA";
import DealerOfferVehicleFeatures from "src/components/MyGarage/TableCompare/DealerOfferVehicleFeatures";
import { AccessoriesState } from "src/store/reducers/accessories";
import { getOfferData, getPaymentPrice } from "src/utils/vehicles";
import { AccountState, DealState, VehicleState } from "Types/state";
import { DealerVehicle, DealerVehicles, padVehicles } from ".";
import GarageHeader from "../../../containers/GarageHeader";
import VehicleHeader from "../VehicleHeader";
import { Feature, getDealerOfferFeatureList, getFeatureList, pipeToObject } from "./lib";
import "./TableCompare.scss";
import VehicleFeatures from "./VehicleFeatures";
import VehiclesList from "./VehiclesList";

export const TRANSLATION_KEY = "MyGarage.TableCompare";

interface TableCompareProps {
    account: AccountState;
    deal: DealState;
    hasAccountAccessories: boolean;
    patchVehiclePayment: any;
    region: string;
    revision: number;
    srpLink?: string;
    uuid: string;
    vehicleAccessories: AccessoriesState;
    vehicles: VehicleState[];
    dealerVehicles: DealerVehicles;
    removeVehicle: (uuid: string, vin: string) => void;
    toggleOSModal?: () => void;
    updateVehicleRating: (uuid: string, vin: string, rating: number, prevRating?: number) => void;
    hasLeaseInGarage: boolean;
    tableRef?: any;
    dealerChanges: string[];
    dealerAddedVehicles: string[];
}

class TableCompare extends Component<TableCompareProps & WithTranslation, any> {
    private vehicleHeaders: Array<VehicleHeader | null> = [];

    constructor(props: TableCompareProps & WithTranslation) {
        super(props);
        this.state = {
            vehicleHeaderHeight: 0,
        };
    }

    public componentDidMount() {
        this.updateVehicleHeaderHeight();
        window.addEventListener("calculatedPaymentUpdated", this.updateVehicleHeaderHeight);
        window.addEventListener("resize", this.updateVehicleHeaderHeight);
    }

    public componentWillUnmount() {
        window.removeEventListener("calculatedPaymentUpdated", this.updateVehicleHeaderHeight);
        window.removeEventListener("resize", this.updateVehicleHeaderHeight);
    }

    public render() {
        const { vehicleHeaderHeight } = this.state;
        const {
            deal,
            hasAccountAccessories,
            vehicles,
            t,
            region,
            account,
            dealerVehicles,
            hasLeaseInGarage,
            dealerChanges,
            dealerAddedVehicles,
        } = this.props;

        let features = getFeatureList(account, hasAccountAccessories, hasLeaseInGarage, dealerChanges);
        let dealerOfferFeatures = getDealerOfferFeatureList(
            account,
            hasAccountAccessories,
            dealerVehicles,
            hasLeaseInGarage,
            dealerChanges
        );

        const showIncentives =
            vehicles.filter((v) => !v.payments?.dealer).some((v) => this.hasIncentives(v)) ||
            Object.values(dealerVehicles).some((v) => v.incentivesTotal);
        if (!showIncentives) {
            features = features.filter((f) => f.id !== "Incentives" && f.id !== "PurchasePrice");
            dealerOfferFeatures = dealerOfferFeatures.filter((f) => f.id !== "Incentives" && f.id !== "PurchasePrice");
        }

        const showMSRP =
            vehicles.filter((v) => !v.payments?.dealer).some((v) => this.shouldShowMSRP(v)) ||
            Object.values(dealerVehicles).some((v) => this.shouldShowMSRP(v, true));
        if (!showMSRP) {
            features = features.filter((f) => f.id !== "MSRP");
            dealerOfferFeatures = dealerOfferFeatures.filter((f) => f.id !== "MSRP");
        }

        const showPaymentFrequency = vehicles
            .filter((v) => !v.payments?.dealer)
            .some((v) => this.hasPaymentFrequencyOptions(v, account));
        if (!showPaymentFrequency) {
            features = features.filter((f) => f.id !== "Frequency");
            dealerOfferFeatures = dealerOfferFeatures.filter((f) => f.id !== "Frequency");
        }

        const filterDealerDiscount =
            account?.integration_settings?.Financing?.ui?.dealer_discount_row &&
            vehicles.every((vehicle) => {
                const dealerDiscount =
                    vehicle.payments?.dealer?.dealer_discount ??
                    vehicle.payments[vehicle.payments.selected]?.dealer_discount;
                return dealerDiscount === null || dealerDiscount === undefined || dealerDiscount === 0;
            });

        if (filterDealerDiscount) {
            features = features.filter((f) => f.id !== "DealerDiscount");
            dealerOfferFeatures = dealerOfferFeatures.filter((f) => f.id !== "DealerDiscount");
        }

        if (!hasLeaseInGarage) {
            features = features.filter((feature) => {
                return feature.id !== "Mileage";
            });
            dealerOfferFeatures = dealerOfferFeatures.filter((feature) => {
                return feature.id !== "Mileage";
            });
        }

        // IDEA: one day, make this smarter... Matt's suggested a memoized selector, I agree.
        const paddedSortedVehicles = padVehicles([
            ...vehicles.filter((v) => v?.payments?.dealer && !v?.gone),
            ...vehicles.filter((v) => dealerAddedVehicles.includes(v?.vin) && !v?.payments?.dealer && !v?.gone),
            ...vehicles.filter((v) => !v?.payments?.dealer && !dealerAddedVehicles.includes(v?.vin) && !v?.gone),
            ...vehicles.filter((v) => v?.gone),
        ]);

        const featureItems = features.map((feature, i) => {
            const toBold = feature.id === "PurchasePrice" ? "TableCompare__features-bold" : "";
            return (
                <li className={`TableCompare__features-item ${toBold}`} key={`feature${feature}${i}`}>
                    {typeof feature.label === "function" ? feature.label(vehicles, region) : feature.label}
                </li>
            );
        });

        return (
            <div className="TableCompare" ref={this.props.tableRef}>
                <h1 className="TableCompare__title">
                    {deal.person && deal.person.first_name
                        ? t(`${TRANSLATION_KEY}.myGarage`, { firstName: deal.person.first_name })
                        : t(`${TRANSLATION_KEY}.yourGarage`)}
                </h1>
                <div className="TableCompare__features">
                    <GarageHeader className="TableCompare__header" style={{ height: vehicleHeaderHeight }} />
                    <ul className="TableCompare__features-list" data-e2e="tableCompareLabels">
                        <li className="TableCompare__features-banner">{t(`${TRANSLATION_KEY}.yourDeals`)}</li>
                        {featureItems}
                    </ul>
                </div>
                <div className="TableCompare__vehicles-wrapper">
                    <div className="TableCompare__vehicles-container" data-e2e="tableCompareVehiclesList">
                        <VehiclesList items={paddedSortedVehicles}>
                            {this.renderVehicleItem(features, dealerOfferFeatures, dealerAddedVehicles)}
                        </VehiclesList>
                    </div>
                </div>
            </div>
        );
    }

    private hasIncentives(vehicle: VehicleState): boolean {
        const offer = getOfferData(vehicle);
        if (!offer) return false;
        return offer.rebates.length > 0;
    }

    private shouldShowMSRP(vehicle: VehicleState | DealerVehicle, isDealerVehicle: boolean = false): boolean {
        return (
            vehicle.msrp !==
            (isDealerVehicle ? (vehicle as DealerVehicle).price : getPaymentPrice(vehicle as VehicleState))
        );
    }

    private hasPaymentFrequencyOptions(vehicle: VehicleState, account: AccountState): boolean {
        const paymentFrequencies = account?.integration_settings?.Financing.payment_frequencies;
        const enabledFrequencies = paymentFrequencies?.[vehicle.type]?.[vehicle.payments.selected];

        return (
            (Object.values({
                monthly: enabledFrequencies?.monthly,
                biweekly: enabledFrequencies?.biweekly,
                weekly: enabledFrequencies?.weekly,
            })?.reduce((optionsEnabled, option) => optionsEnabled + (option ? 1 : 0), 0) ?? 0) > 1
        );
    }

    private renderVehicleItem = (features: Feature[], dealerFeatures: Feature[], dealerAddedVehicles: string[]) => (
        vehicle: VehicleState,
        index: number
    ) => {
        return (
            <div className="TableCompare__vehicle" data-vin={vehicle?.vin}>
                {this.renderVehicleHeader(vehicle, index, dealerAddedVehicles)}
                {vehicle?.payments?.dealer
                    ? this.renderDealerOfferVehicleFeaturesList(dealerFeatures, vehicle)
                    : this.renderVehicleFeaturesList(
                          features,
                          // tslint:disable-next-line
                          vehicle === null || vehicle.loading === true ? ((null as unknown) as VehicleState) : vehicle
                      )}
            </div>
        );
    };

    private renderVehicleHeader = (vehicle: VehicleState, index: number, dealerAddedVehicles: string[]) => {
        const { vehicleHeaderHeight } = this.state;
        return (
            <VehicleHeader
                account={this.props.account}
                dealId={this.props.deal.uuid}
                dealState={""}
                hideRemove={this.props.vehicles.length === 1}
                isPaymentsLoading={
                    vehicle?.isUpdating ||
                    vehicle?.payments?.[vehicle?.payments?.selected as string]?.data_hash !==
                        vehicle?.payments?.[vehicle?.payments?.selected as string]?.request_hash ||
                    vehicle?.loading
                }
                minHeight={vehicleHeaderHeight}
                onUpdate={this.updateVehicleHeaderHeight}
                ref={(element) => this.setVehicleHeaderRef(element, index)}
                removeVehicle={this.onDelete}
                srpLink={this.props.srpLink}
                toggleOSModal={this.props.toggleOSModal}
                updateVehicleRating={this.props.updateVehicleRating}
                vehicle={vehicle}
                isDealerAddedVehicle={!!vehicle?.vin && dealerAddedVehicles.includes(vehicle?.vin)}
            />
        );
    };

    private renderVehicleFeaturesList = (features: Feature[], vehicle: VehicleState) => {
        const { vehicleAccessories } = this.props;

        return (
            <ul className="TableCompare__vehicle" data-e2e="tableCompareVehicleFeatures">
                <li className="TableCompare__features-banner" />
                <VehicleFeatures
                    featureList={features}
                    onUpdate={this.onUpdate}
                    vehicle={vehicle}
                    vehicleAccessories={vehicleAccessories}
                />
            </ul>
        );
    };

    private renderDealerOfferVehicleFeaturesList = (features: Feature[], vehicle: VehicleState) => {
        return (
            <ul className="TableCompare__vehicle" data-e2e="tableCompareDealerOfferVehicleFeatures">
                <li className="TableCompare__features-banner" />
                <DealerOfferVehicleFeatures featureList={features} vehicle={vehicle} />
            </ul>
        );
    };

    private setVehicleHeaderRef = (element: VehicleHeader | null, index: number) => {
        this.vehicleHeaders[index] = element;
    };

    private updateVehicleHeaderHeight = (event?: any) => {
        this.setState({ vehicleHeaderHeight: 0 }, () => {
            const vehiclesHeaderHeight = this.vehicleHeaders.map((vehicleHeader) => {
                const vehicleHeaderDOMNode = vehicleHeader && (findDOMNode(vehicleHeader) as HTMLDivElement);
                return (vehicleHeaderDOMNode && vehicleHeaderDOMNode.offsetHeight) || 0;
            });

            const maxVehicleHeaderHeight = Math.max(...vehiclesHeaderHeight);
            const validMaxVehicleHeaderHeight =
                !isNaN(maxVehicleHeaderHeight) && isFinite(maxVehicleHeaderHeight) && maxVehicleHeaderHeight > 0;

            if (validMaxVehicleHeaderHeight) {
                this.setState({ vehicleHeaderHeight: maxVehicleHeaderHeight });
            }
        });
    };

    private onUpdate = (vin: string, key: string, value: any, valueFormat: any) => {
        const body = pipeToObject(key, value, valueFormat);
        const vehicle = this.props.vehicles.find((v) => {
            return v.vin === vin;
        }) as VehicleState;
        this.props.patchVehiclePayment(body.payments, this.props.uuid, vehicle);
        analyticsFEA.engine.calculationStart(this.props.uuid, [vehicle]);
    };

    private onDelete = (vin: string) => {
        if (this.props.vehicles.length > 1) {
            this.props.removeVehicle(this.props.uuid, vin);
        }
    };
}

export default withTranslation()(TableCompare);
